import React, { useState } from 'react';
import { SpeakerIcon, PlusIcon, XIcon, SupportIcon } from './icons/ChatIcons';
import { TTSMethod, BudgetWarning } from '../../contexts/ChatContext';
import { useChat } from '../../contexts/ChatContext';
import { formatNumber } from '../../utils/formatNumber';
import { SupportStatus } from '../../types/chat';
import { useAuth } from '../../contexts/AuthContext';
import { ISOTIPO_BLANCO } from '../landing/brandAssets';

interface ChatHeaderProps {
  onClose: () => void;
  onNewChat?: () => void;
  title?: string;
  showNewChatButton?: boolean;
  autoSpeakEnabled?: boolean;
  onToggleAutoSpeak?: () => void;
  ttsMethod?: TTSMethod;
  onToggleTTSMethod?: () => void;
  budgetWarning?: BudgetWarning | null;
  onClearBudgetWarning?: () => void;
  // Handoff a humano
  supportStatus?: SupportStatus;
  onStartHandoff?: () => void;
  onCloseHandoff?: () => void;
}

const ChatHeader: React.FC<ChatHeaderProps> = ({
  onClose,
  onNewChat,
  title = 'Asistente de MiCampoDigital',
  showNewChatButton = true,
  autoSpeakEnabled = false,
  onToggleAutoSpeak,
  ttsMethod = 'native',
  onToggleTTSMethod,
  budgetWarning,
  onClearBudgetWarning,
  supportStatus = 'idle',
  onStartHandoff,
  onCloseHandoff,
}) => {
  const { user } = useAuth();
  const [showTooltip, setShowTooltip] = useState(false);

  const isHandoffActive =
    supportStatus === 'queued' ||
    supportStatus === 'live' ||
    supportStatus === 'requesting' ||
    supportStatus === 'capture';

  // Etiqueta y estilo del badge según el estado del handoff.
  const handoffBadge = (): { label: string; className: string } => {
    switch (supportStatus) {
      case 'live':
        return { label: 'en vivo', className: 'bg-success/90 text-paper' };
      case 'queued':
        return { label: 'en cola', className: 'bg-trigo text-grafito' };
      case 'requesting':
        return { label: 'conectando…', className: 'bg-info/90 text-paper' };
      case 'capture':
        return { label: 'capturando', className: 'bg-info/90 text-paper' };
      default:
        return { label: '', className: '' };
    }
  };

  return (
    // Verde Campo en los dos temas (bg-chrome): el isotipo blanco necesita ≥ 3:1 de contraste.
    <div className="bg-chrome text-white px-4 py-3 flex items-center justify-between rounded-t-lg border-b border-hairline">
      <div className="flex items-center space-x-3">
        {isHandoffActive ? (
          <div className="rounded-md bg-white/15 p-2">
            <SupportIcon className="h-5 w-5" />
          </div>
        ) : (
          <img src={ISOTIPO_BLANCO} alt="" className="h-8 w-auto" />
        )}

        <div>
          <h3 className="font-display font-semibold text-sm">
            {isHandoffActive ? 'Atención al cliente' : title}
          </h3>
          <p className="text-[10px] text-white/70">
            {isHandoffActive && supportStatus !== 'capture'
              ? 'Conversando con el equipo de soporte'
              : !user
                ? 'Modo invitado — iniciá sesión para consultar tu campo'
                : ttsMethod === 'elevenlabs'
                  ? 'ElevenLabs (Voz AI)'
                  : 'Voz nativa del navegador'
            }
          </p>
        </div>
      </div>

      <div className="flex items-center space-x-2">
        {/* Budget Warning Icon con Tooltip */}
        {budgetWarning && (
          <div className="relative">
            <button
              onMouseEnter={() => setShowTooltip(true)}
              onMouseLeave={() => setShowTooltip(false)}
              onClick={onClearBudgetWarning}
              className="p-2 rounded-md bg-trigo text-grafito hover:bg-trigo/90 transition-colors relative animate-pulse"
              aria-label="Advertencia de presupuesto"
            >
              {/* Icono de advertencia (triángulo con exclamación) */}
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
            </button>

            {/* Tooltip */}
            {showTooltip && (
              <div className="absolute bottom-full right-0 mb-2 w-64 bg-ink text-paper text-xs rounded-lg p-3 shadow-overlay z-50">
                <div className="flex items-start space-x-2">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-trigo flex-shrink-0 mt-0.5" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                  <div className="flex-1">
                    <p className="font-semibold mb-1">Advertencia de Presupuesto</p>
                    <p className="text-paper/80">{budgetWarning.message}</p>
                    <p className="text-paper mt-2 font-semibold">
                      {formatNumber(budgetWarning.percentage, { decimals: 1 })}% utilizado
                    </p>
                    <p className="text-xs text-paper/60 mt-1">
                      Clic para descartar
                    </p>
                  </div>
                </div>
                {/* Flecha del tooltip */}
                <div className="absolute top-full right-4 -mt-1 border-4 border-transparent" style={{ borderTopColor: 'var(--cm-ink)' }}></div>
              </div>
            )}
          </div>
        )}

        {onToggleAutoSpeak && (
          <button
            onClick={onToggleAutoSpeak}
            className={`p-2 rounded-md transition-colors ${
              autoSpeakEnabled
                ? 'bg-trigo text-grafito hover:bg-trigo/90'
                : 'hover:bg-white/10'
            }`}
            aria-label={autoSpeakEnabled ? 'Desactivar lectura automática' : 'Activar lectura automática'}
            title={autoSpeakEnabled ? 'Lectura automática activada' : 'Activar lectura automática'}
          >
            <SpeakerIcon className="h-5 w-5" />
          </button>
        )}

        {onToggleTTSMethod && (
          <button
            onClick={onToggleTTSMethod}
            className={`p-2 rounded-md transition-colors ${
              ttsMethod === 'elevenlabs'
                ? 'bg-info text-paper hover:bg-info/90'
                : 'hover:bg-white/10'
            }`}
            aria-label="Cambiar método de reproducción"
            title={
              ttsMethod === 'native'
                ? 'Usar ElevenLabs (calidad premium)'
                : 'Volver a voz nativa'
            }
          >
            <span className="text-xs font-bold">
              {ttsMethod === 'native' ? '🔊' : 'AI'}
            </span>
          </button>
        )}

        {showNewChatButton && onNewChat && !isHandoffActive && (
          <button
            onClick={onNewChat}
            className="p-2 hover:bg-white/10 rounded-md transition-colors"
            aria-label="Nueva conversación"
            title="Nueva conversación"
          >
            <PlusIcon className="h-5 w-5" />
          </button>
        )}

        {/* Handoff a humano: botón "Hablar con una persona" (estado idle) o
            badge de estado + botón Cerrar (estado activo). */}
        {!isHandoffActive && onStartHandoff && (
          <button
            onClick={onStartHandoff}
            data-ar-guide="chat-handoff"
            className="p-2 hover:bg-white/10 rounded-md transition-colors"
            aria-label="Hablar con una persona"
            title="Hablar con atención al cliente"
          >
            <SupportIcon className="h-5 w-5" />
          </button>
        )}
        {isHandoffActive && (
          <>
            {handoffBadge().label && (
              <span
                className={`text-[10px] font-semibold px-2 py-0.5 rounded ${handoffBadge().className}`}
              >
                {handoffBadge().label}
              </span>
            )}
            {onCloseHandoff && (
              <button
                onClick={onCloseHandoff}
                className="text-xs px-2 py-1 rounded bg-white/15 hover:bg-white/25 transition-colors"
                title="Cerrar atención"
              >
                Cerrar
              </button>
            )}
          </>
        )}

        <button
          onClick={onClose}
          className="p-2 hover:bg-white/10 rounded-md transition-colors"
          aria-label="Cerrar chat"
        >
          <XIcon className="h-5 w-5" />
        </button>
      </div>
    </div>
  );
};

export default ChatHeader;
